import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { API } from "../../lib/api";

const FALLBACK_REVIEWS = [
    {
        author_name: "Priya Sharma",
        rating: 5,
        text: "Commmmuniti transformed our brand presence completely. Their team is professional, creative, and truly understands digital marketing. Highly recommended!",
        time: "2 weeks ago",
    },
    {
        author_name: "Rahul Verma",
        rating: 5,
        text: "Best digital marketing agency in Indore. The results we got from our campaigns were beyond expectations. Great strategy and execution.",
        time: "1 month ago",
    },
    {
        author_name: "Anjali Mehta",
        rating: 5,
        text: "Amazing team! They helped us build a strong online presence. The creativity and dedication they put into every project is unmatched.",
        time: "2 months ago",
    },
    {
        author_name: "Vikram Singh",
        rating: 4,
        text: "Solid agency with a clear, data-driven approach. Our lead quality improved noticeably and the team was always responsive throughout.",
        time: "3 months ago",
    },
    {
        author_name: "Neha Kapoor",
        rating: 5,
        text: "From branding to performance campaigns, Commmmuniti delivered on every promise. Smooth collaboration and great results.",
        time: "3 months ago",
    },
];

async function fetchGoogleReviews() {
    try {
        const res = await fetch(`${API}/reviews`);
        if (!res.ok) return null;
        const data = await res.json();
        if (Array.isArray(data) && data.length) {
            return data;
        }
    } catch (e) {
        console.warn("Failed to fetch Google reviews:", e);
    }
    return null;
}

function formatTime(time) {
    if (typeof time === "number") {
        const diff = Date.now() / 1000 - time;
        const days = Math.floor(diff / 86400);
        if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
        const years = Math.floor(months / 12);
        return `${years} year${years === 1 ? "" : "s"} ago`;
    }
    return time;
}

export default function ClientReviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const data = await fetchGoogleReviews();
                if (active) {
                    setReviews(data && data.length ? data : FALLBACK_REVIEWS);
                    setLoading(false);
                }
            } catch (e) {
                if (active) {
                    setReviews(FALLBACK_REVIEWS);
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, []);

    return (
        <section
            id="client-reviews"
            data-testid="client-reviews-section"
            className="relative bg-white py-24 lg:py-32"
        >
            <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 items-end pb-16">
                    <div className="lg:col-span-4">
                        <p className="label-eyebrow">Google Reviews</p>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-communiti-text leading-[0.95]"
                        >
                            Hear from our
                            <br />
                            <span className="block font-bold uppercase text-communiti">
                                CLIENTS
                            </span>
                        </motion.h2>
                    </div>
                    <div className="lg:col-span-8 lg:justify-self-end">
                        <p className="max-w-md text-sm text-communiti-muted leading-relaxed">
                            What our clients say about working with Commmmuniti.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center gap-3 py-12 text-communiti-muted">
                        <div className="h-5 w-5 border-2 border-communiti border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Loading reviews…</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {reviews.map((review, i) => (
                            <motion.div
                                key={i}
                                data-testid={`client-review-card-${i}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.7,
                                    delay: i * 0.1,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="flex flex-col gap-4 rounded-2xl border border-communiti-line bg-communiti-surface/40 p-6 hover:border-communiti hover:-translate-y-1 transition-all duration-300"
                            >
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: 5 }).map((_, idx) => (
                                        <Star
                                            key={idx}
                                            className={`h-4 w-4 ${
                                                idx < review.rating
                                                    ? "fill-yellow-400 text-yellow-400"
                                                    : "text-communiti-line"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <p className="text-sm text-communiti-text leading-relaxed line-clamp-4">
                                    "{review.text}"
                                </p>
                                <div className="mt-auto pt-4 border-t border-communiti-line">
                                    <p className="font-display text-sm font-bold text-communiti-text">
                                        {review.author_name}
                                    </p>
                                    <p className="text-xs text-communiti-muted mt-1">
                                        {formatTime(review.time)}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
